import { ShieldCheck } from "@phosphor-icons/react";
import LegalLayout, { Section, P } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="Politique de confidentialité"
      icon={ShieldCheck}
      title="Vos données de santé, entre de bonnes mains."
      intro="Cette page explique quelles données Questiomed traite, dans quel but, pendant combien de temps, avec quels tiers, et quels droits vous avez sur elles. Elle complète le consentement sollicité avant chaque questionnaire."
      path="/confidentialite"
    >
      <Section title="1. Responsables du traitement">
        <p>
          Deux acteurs se partagent les responsabilités selon la nature des données :
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            <strong>Le praticien</strong> qui vous envoie le questionnaire est <strong>responsable du traitement</strong>
            {" "}au sens de l'article 4-7 du RGPD, pour les données de santé collectées à travers
            vos réponses. Son identité vous est indiquée avant chaque questionnaire.
          </li>
          <li>
            <strong>Questiomed</strong>, édité par <P />, agit en qualité de
            <strong> sous-traitant</strong> au sens de l'article 28 du RGPD. Il fournit la
            plateforme technique permettant au praticien d'exercer son activité, sans utiliser
            vos données pour son propre compte.
          </li>
        </ul>
        <p>
          Pour toute question, vous pouvez contacter le DPO de Questiomed :
          <a href="mailto:dpo@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">dpo@questiomed.fr</a>.
        </p>
      </Section>

      <Section title="2. Données collectées">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Données d'identité (patient)</strong> — nom, prénom, date de naissance (renseignés par le praticien pour rattacher les réponses à votre dossier), email (facultatif, uniquement pour l'envoi du questionnaire).</li>
          <li><strong>Données de santé</strong> — les réponses saisies volontairement, ainsi que les scores et interprétations calculés automatiquement à partir de ces réponses.</li>
          <li><strong>Données de connexion / usage (praticien)</strong> — email, mot de passe haché, horodatage des accès, adresse IP de connexion, journal d'audit.</li>
          <li><strong>Données de facturation (praticien)</strong> — raison sociale, adresse, historique de facturation. Les données bancaires ne sont ni collectées ni conservées par Questiomed : elles transitent par Stripe.</li>
          <li><strong>Métadonnées techniques</strong> — date et heure de complétion, méthode d'envoi (email / lien / QR / consultation).</li>
        </ul>
        <p className="text-[13px] text-[#5F758F] italic">
          Aucune donnée patient n'est enrichie par des sources externes ou revendue à un tiers.
        </p>
      </Section>

      <Section title="3. Finalités du traitement">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Aide au diagnostic et au suivi médical par le praticien.</li>
          <li>Comparaison longitudinale de vos scores pour un même questionnaire dans le temps.</li>
          <li>Préparation et compte-rendu de consultation.</li>
          <li>Gestion du compte, facturation et support technique (praticien).</li>
          <li>Sécurité du Service : détection des tentatives de fraude et respect des obligations légales.</li>
          <li>Statistiques anonymisées à visée de recherche clinique, uniquement à l'initiative du praticien et sans que vous soyez identifiable.</li>
        </ul>
      </Section>

      <Section title="4. Bases légales">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Art. 6-1-a et 9-2-a RGPD</strong> — votre consentement explicite, recueilli avant chaque questionnaire.</li>
          <li><strong>Art. 9-2-h RGPD</strong> — traitement nécessaire à la médecine préventive, au diagnostic et à la prise en charge sanitaire.</li>
          <li><strong>Art. 6-1-b RGPD</strong> — exécution du contrat pour les données praticien.</li>
          <li><strong>Art. 6-1-c RGPD</strong> — obligation légale de conservation comptable / dossier médical.</li>
        </ul>
      </Section>

      <Section title="5. Sous-traitants et partenaires">
        <p>
          Questiomed fait appel à des sous-traitants sélectionnés pour leurs garanties de sécurité
          et de conformité RGPD :
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px] border-collapse mt-3">
            <thead>
              <tr className="border-b border-[#E1E9F0] text-left text-[#5F758F]">
                <th className="py-2 pr-4 font-semibold">Prestataire</th>
                <th className="py-2 pr-4 font-semibold">Finalité</th>
                <th className="py-2 pr-4 font-semibold">Localisation</th>
              </tr>
            </thead>
            <tbody className="text-[#1E3A5F]">
              <tr className="border-b border-[#E1E9F0]"><td className="py-2 pr-4"><P /></td><td className="py-2 pr-4">Hébergement HDS des données du Service</td><td className="py-2 pr-4">France</td></tr>
              <tr className="border-b border-[#E1E9F0]"><td className="py-2 pr-4">Stripe Payments Europe Ltd.</td><td className="py-2 pr-4">Encaissement des abonnements praticiens</td><td className="py-2 pr-4">Irlande (UE)</td></tr>
              <tr className="border-b border-[#E1E9F0]"><td className="py-2 pr-4">Resend Inc.</td><td className="py-2 pr-4">Envoi des emails transactionnels</td><td className="py-2 pr-4">UE (routage transactionnel)</td></tr>
              <tr className="border-b border-[#E1E9F0]"><td className="py-2 pr-4">Anthropic PBC (Claude)</td><td className="py-2 pr-4">Extraction structurée de documents cliniques (fonction Premium, sur demande explicite du praticien)</td><td className="py-2 pr-4">Union européenne / États-Unis avec clauses contractuelles types</td></tr>
              <tr><td className="py-2 pr-4">Google Ireland Ltd. (SSO)</td><td className="py-2 pr-4">Authentification optionnelle par compte Google</td><td className="py-2 pr-4">Irlande (UE)</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-[13px] text-[#5F758F] italic mt-3">
          Aucun sous-traitant n'a accès aux données patients à des fins commerciales. Tous sont
          liés à Questiomed par un contrat conforme à l'article 28 du RGPD.
        </p>
      </Section>

      <Section title="6. Durée de conservation">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Réponses aux questionnaires</strong> : conservées jusqu'à <strong>20 ans à compter du dernier acte médical</strong> conformément à l'article R. 1112-7 du Code de la santé publique.</li>
          <li><strong>Compte praticien</strong> : durée de l'abonnement, plus 3 ans après la dernière connexion, sauf demande de suppression anticipée.</li>
          <li><strong>Factures</strong> : 10 ans (obligation comptable — Art. L. 123-22 du Code de commerce).</li>
          <li><strong>Journal d'audit</strong> : 12 mois (recommandation CNIL — délibération 2018-303).</li>
          <li><strong>Cookies</strong> : 13 mois maximum (session <em>httpOnly</em> renouvelée).</li>
        </ul>
      </Section>

      <Section title="7. Localisation et transferts">
        <p>
          Les données sont hébergées <strong>en France</strong> chez un tiers certifié HDS. Aucun
          transfert de données de santé n'est réalisé hors Union européenne. Les rares
          sous-traitants dont la maison mère est située aux États-Unis (par exemple Anthropic pour
          la fonction d'analyse IA de documents Premium) opèrent sous clauses contractuelles types
          (Art. 46 RGPD) et ne reçoivent que des données strictement nécessaires, temporairement.
        </p>
      </Section>

      <Section title="8. Sécurité">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Chiffrement en transit (HTTPS/TLS 1.2+).</li>
          <li>Chiffrement au repos des données de santé.</li>
          <li>Cookies de session <em>HttpOnly Secure SameSite</em> — jamais accessibles à JavaScript.</li>
          <li>Mots de passe praticiens hachés avec bcrypt + verrouillage anti-force brute (5 tentatives, verrouillage 15 min).</li>
          <li>Journal d'audit systématique des accès aux données patients.</li>
          <li>Sauvegardes chiffrées quotidiennes, avec plan de reprise d'activité testé.</li>
          <li>Revue de sécurité continue et procédure de notification d'incident conforme à l'article 33 du RGPD.</li>
        </ul>
      </Section>

      <Section title="9. Cookies et traceurs">
        <p>
          Questiomed utilise uniquement des cookies dits « strictement nécessaires » à
          l'authentification et au bon fonctionnement du Service. Ils ne nécessitent pas de
          consentement préalable (Art. 82 loi Informatique et Libertés).
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><code className="text-[13px] bg-[#F4F7FB] px-1.5 py-0.5 rounded">access_token</code> / <code className="text-[13px] bg-[#F4F7FB] px-1.5 py-0.5 rounded">session_token</code> — session d'authentification praticien (30 jours max, <em>HttpOnly</em>).</li>
        </ul>
        <p>
          Aucun cookie publicitaire, analytique ou de mesure d'audience tiers n'est déposé.
        </p>
      </Section>

      <Section title="10. Vos droits">
        <p>Conformément aux articles 15 à 22 du RGPD, vous disposez :</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>d'un droit d'<strong>accès</strong> à vos données ;</li>
          <li>d'un droit de <strong>rectification</strong> ;</li>
          <li>d'un droit d'<strong>effacement</strong> (« droit à l'oubli ») ;</li>
          <li>d'un droit de <strong>limitation</strong> du traitement ;</li>
          <li>d'un droit à la <strong>portabilité</strong> de vos données ;</li>
          <li>d'un droit d'<strong>opposition</strong> pour motif légitime ;</li>
          <li>d'un droit de <strong>retirer votre consentement</strong> à tout moment, sans que cela ne remette en cause la licéité du traitement antérieur ;</li>
          <li>d'un droit de définir des <strong>directives post-mortem</strong> quant au devenir de vos données.</li>
        </ul>
        <p>
          Pour exercer ces droits, contactez en premier lieu votre praticien : il dispose dans son
          interface d'outils dédiés d'anonymisation et de suppression. À défaut de réponse,
          adressez votre demande au DPO :
          <a href="mailto:dpo@questiomed.fr" className="text-[#3A8FA5] underline underline-offset-2 ml-1">dpo@questiomed.fr</a>,
          accompagnée d'un justificatif d'identité. Un délai maximal d'un (1) mois s'applique
          pour la réponse.
        </p>
      </Section>

      <Section title="11. Réclamation">
        <p>
          En cas de désaccord persistant, vous pouvez saisir la Commission Nationale de
          l'Informatique et des Libertés (CNIL) :
        </p>
        <p className="rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3">
          3 place de Fontenoy — TSA 80715 — 75334 Paris Cedex 07<br />
          Téléphone : 01 53 73 22 22<br />
          Web : <a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="text-[#3A8FA5] underline underline-offset-2">cnil.fr</a>
        </p>
      </Section>

      <Section title="12. Modification de la politique">
        <p>
          Toute évolution substantielle de la présente politique sera notifiée aux utilisateurs
          au moins trente (30) jours à l'avance par email et via l'application. La date de
          dernière mise à jour figure au bas de cette page.
        </p>
      </Section>
    </LegalLayout>
  );
}
